import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { Map } from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router-dom';
import { Property } from '../../utils/properties';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBed, faBuilding, faRulerCombined, faHeart } from "@fortawesome/free-solid-svg-icons";
import useTokenStore from "../../store/store";
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import { doesThemeSkipLogin } from "../../store/themesWithoutLogin";
import { numberFormat } from "../../utils/properties";
import { toggleFavoriteProperty } from '../../utils/favourites';
import { useFavoritesStore } from '../../store/favoritesStore';
import { resolveValuationDisplay, pickValuationAmount } from '../../utils/valuationDisplay';
import "leaflet/dist/leaflet.css";
import L from 'leaflet';

// Fix for default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Default UK center coordinates
const UK_CENTER: [number, number] = [54.5, -2.5];
// UK bounds (more restrictive)
const UK_BOUNDS: [[number, number], [number, number]] = [
  [49.5, -8.5], // Southwest corner
  [60.5, 2.5]   // Northeast corner
];

// Cache for storing postcode to coordinates mappings
const postcodeCache: Record<string, [number, number]> = {};
// Cache for storing invalid postcodes to avoid repeated lookups
const invalidPostcodeCache: Set<string> = new Set();

// Default coordinates for major UK cities (for fallback)
const DEFAULT_COORDS: Record<string, [number, number]> = {
  "London": [51.5074, -0.1278],
  "Manchester": [53.4808, -2.2426],
  "Birmingham": [52.4862, -1.8904],
  "Liverpool": [53.4084, -2.9916],
  "Leeds": [53.8008, -1.5491],
  "Cardiff": [51.4816, -3.1791],
  "Edinburgh": [55.9533, -3.1883],
  "Belfast": [54.5973, -5.9301],
  "Glasgow": [55.8642, -4.2518],
  // Default for any other region
  "Default": UK_CENTER
};

interface PropertyMapProps {
  properties: Property[];
}

const PropertyMap: React.FC<PropertyMapProps> = ({ properties }) => {
  const [, setSelectedProperty] = useState<Property | null>(null);
  const [propertiesWithCoords, setPropertiesWithCoords] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mapCenter] = useState<[number, number]>(UK_CENTER); // Always start with UK center
  const [mapZoom] = useState(5.5); // Lower zoom to show all of UK
  const [mapReady, setMapReady] = useState(false);
  const [allPropertiesLoaded, setAllPropertiesLoaded] = useState(false);
  const mapRef = useRef<Map | null>(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const BATCH_SIZE = 50; // Increased batch size for faster processing
  
  const navigate = useNavigate();
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const { favoriteIds, fetchFavorites } = useFavoritesStore();

  // Helper functions
  const isWhiteOrShade = (color: string) => {
    if (!color) return true;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const getBackgroundAndTextColor = (color: string, fallbackColor: string, defaultTextColor: string) => {
    if (!color || isWhiteOrShade(color)) {
      return { backgroundColor: fallbackColor, textColor: 'white' };
    } else {
      return { backgroundColor: color, textColor: defaultTextColor };
    }
  };


  // Improved postcode extraction function with better regex
  const extractPostcode = (address?: string): string | null => {
    if (!address) return null;
    
    // UK postcode regex pattern - more comprehensive
    const postcodeRegex = /\b([A-Z]{1,2}[0-9][A-Z0-9]?[0-9]?[A-Z]{2})\b|\b([A-Z]{1,2}[0-9][A-Z0-9]?)\s*([0-9][A-Z]{2})\b/gi;
    const matches = address.match(postcodeRegex);
    
    if (matches && matches.length > 0) {
      // Clean up and format the postcode
      return matches[matches.length - 1].replace(/\s+/g, '').toUpperCase();
    }
    
    return null;
  };

  // Get approximate coordinates based on property region
  const getApproximateCoords = (property: Property): [number, number] => {
    // Check if property region matches any of our default cities
    if (property.region) {
      for (const [city, coords] of Object.entries(DEFAULT_COORDS)) {
        if (property.region.includes(city)) {
          return coords;
        }
      }
    }
    
    // If no match in region, try display address
    if (property.propertyOf) {
      for (const [city, coords] of Object.entries(DEFAULT_COORDS)) {
        if (property.propertyOf.includes(city)) {
          return coords;
        }
      }
    }
    
    // Add small random offset to prevent properties from stacking
    const randomOffset = () => (Math.random() - 0.5) * 0.05; // ~1-3 km random offset
    
    // Default to UK center with random offset
    return [UK_CENTER[0] + randomOffset(), UK_CENTER[1] + randomOffset()];
  };

  // Batch geocode postcodes using postcodes.io
  const batchGeocodePostcodes = async (postcodes: string[]): Promise<void> => {
    // Filter out already cached postcodes and known invalid postcodes
    const uniquePostcodesSet = new Set(postcodes);
    const uniquePostcodes: string[] = [];
    
    // Manually filter out cached and invalid postcodes
    uniquePostcodesSet.forEach(code => {
      if (!(code in postcodeCache) && !invalidPostcodeCache.has(code)) {
        uniquePostcodes.push(code);
      }
    });
    
    if (uniquePostcodes.length === 0) return;
    
    console.log(`Geocoding ${uniquePostcodes.length} postcodes`);
    
    try {
      const response = await fetch('https://api.postcodes.io/postcodes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes: uniquePostcodes })
      });
      
      if (!response.ok) {
        console.error(`Geocoding API error: ${response.status}`);
        
        // Mark these as invalid to avoid repeated failed attempts
        uniquePostcodes.forEach(code => {
          invalidPostcodeCache.add(code);
        });
        
        return;
      }
      
      const data = await response.json();
      
      if (data.status === 200 && data.result) {
        let validResults = 0;
        
        // Store results in cache and track invalid postcodes
        data.result.forEach((item: { query: string, result: { latitude: number, longitude: number } | null }) => {
          if (item.result && item.result.latitude && item.result.longitude) {
            postcodeCache[item.query] = [item.result.latitude, item.result.longitude];
            validResults++;
          } else {
            // Mark as invalid to avoid future lookups
            invalidPostcodeCache.add(item.query);
          }
        });
        
        console.log(`Geocoding results: ${validResults} valid, ${uniquePostcodes.length - validResults} invalid`);
      }
    } catch (error) {
      console.error('Error geocoding postcodes:', error);
      
      // Mark these as invalid to avoid repeated failed attempts
      uniquePostcodes.forEach(code => {
        invalidPostcodeCache.add(code);
      });
    }
  };

  // Process properties in batches and assign coordinates
  const processPropertiesBatch = useCallback(async () => {
    if (!properties || properties.length === 0) {
      setIsLoading(false);
      setAllPropertiesLoaded(true);
      setMapReady(true);
      return;
    }
    
    // Set total count on first run
    if (currentPage === 1) {
      console.log(`Starting to process ${properties.length} properties`);
      setTotalCount(properties.length);
    }
    
    // Calculate the slice of properties to process in this batch
    const startIndex = (currentPage - 1) * BATCH_SIZE;
    const endIndex = Math.min(startIndex + BATCH_SIZE, properties.length);
    const batch = properties.slice(startIndex, endIndex);
    
    if (batch.length === 0 || processedCount >= properties.length) {
      // We've processed all properties
      setIsLoading(false);
      setAllPropertiesLoaded(true);
      setMapReady(true);
      console.log("No more properties to process");
      return;
    }
    
    console.log(`Processing batch ${currentPage}: ${batch.length} properties (${startIndex}-${endIndex} of ${properties.length})`);
    
    // First pass: collect postcodes from this batch
    const postcodes: string[] = [];
    const existingPropertyIds = new Set(propertiesWithCoords.map(p => p._id));
    
    batch.forEach(property => {
      // Skip if property already has valid coordinates
      if (property.latitude && property.longitude && 
          !isNaN(Number(property.latitude)) && 
          !isNaN(Number(property.longitude))) {
        return;
      }
      
      // Try to extract postcode from propertyOf or stored postcode
      const postcode = extractPostcode(property.propertyOf) ||
                       property.postcode;
                       
      if (postcode && !(postcode in postcodeCache) && !invalidPostcodeCache.has(postcode)) {
        postcodes.push(postcode);
      }
    });
    
    // If we have postcodes to geocode, do it in batch
    if (postcodes.length > 0) {
      await batchGeocodePostcodes(postcodes);
    }
    
    // Second pass: assign coordinates to properties in this batch
    let usingFallbackCount = 0;
    
    const enhancedProperties = batch.map(property => {
      // Skip if we already have this property
      if (existingPropertyIds.has(property._id)) {
        return null;
      }
      
      // If property already has valid coordinates, use them
      if (property.latitude && property.longitude && 
          !isNaN(Number(property.latitude)) && 
          !isNaN(Number(property.longitude))) {
        return property;
      }
      
      // Try to get coordinates from cache using postcode
      const postcode = extractPostcode(property.propertyOf) ||
                      property.postcode;
      
      let coords: [number, number];
      
      if (postcode && postcode in postcodeCache) {
        // Use cached coordinates from postcode
        coords = postcodeCache[postcode];
      } else {
        // Fall back to approximate coordinates
        coords = getApproximateCoords(property);
        usingFallbackCount++;
      }
      
      // Create a new property object with the coordinates
      return {
        ...property,
        latitude: coords[0],
        longitude: coords[1]
      };
    }).filter((prop): prop is Property => prop !== null);
    
    // Update the full list of properties with coordinates
    if (enhancedProperties.length > 0) {
      setPropertiesWithCoords(prevProps => [...prevProps, ...enhancedProperties]);
      console.log(`Added ${enhancedProperties.length} properties to the map (${usingFallbackCount} using fallback coordinates)`);
    }
    
    // Update the processed count
    const newProcessedCount = startIndex + batch.length;
    setProcessedCount(newProcessedCount);
    
    // If we've processed all properties, we're done
    if (newProcessedCount >= properties.length) {
      console.log(`Finished processing all ${properties.length} properties`);
      console.log(`Final count of properties with coords: ${propertiesWithCoords.length + enhancedProperties.length}`);
      
      // We've processed everything, update loading state
      setIsLoading(false);
      setAllPropertiesLoaded(true);
      setMapReady(true);
    } else {
      // Move to the next page
      setCurrentPage(prevPage => prevPage + 1);
    }
  }, [properties, currentPage, processedCount, propertiesWithCoords]);

  // Start processing when properties change
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // Reset when properties array changes completely
    if (properties && (!propertiesWithCoords.length || properties.length < propertiesWithCoords.length)) {
      console.log("New set of properties detected, starting fresh");
      setPropertiesWithCoords([]);
      setIsLoading(true);
      setAllPropertiesLoaded(false);
      setProcessedCount(0);
      setCurrentPage(1);
    } else if (properties) {
      // Check if we have completely new properties to add
      const currentPropertyIds = new Set(properties.map(p => p._id));
      const existingPropertyIds = new Set(propertiesWithCoords.map(p => p._id));
      
      // Count how many new properties we have
      let newPropertyCount = 0;
      currentPropertyIds.forEach(id => {
        if (!existingPropertyIds.has(id)) {
          newPropertyCount++;
        }
      });
      
      if (newPropertyCount > 0) {
        console.log(`${newPropertyCount} new properties to add to existing collection`);
        setIsLoading(true);
        setAllPropertiesLoaded(false);
        setProcessedCount(0);
        setCurrentPage(1);
      }
    }
  }, [properties]);
  /* eslint-enable react-hooks/exhaustive-deps */
  
  // Process next batch when current page changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isLoading) {
      processPropertiesBatch();
    }
  }, [currentPage, isLoading, processPropertiesBatch, propertiesWithCoords]);

  // Add cleanup effect for loading state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // If we've processed all properties and the map is ready, ensure loading state is false
    if (allPropertiesLoaded && mapReady) {
      setIsLoading(false);
    }
  }, [allPropertiesLoaded, mapReady]);

  // Cleanup function for map
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.remove();
          mapRef.current = null;
        } catch (error) {
          console.log('Error during map cleanup:', error);
        }
      }
    };
  }, []);

  const handlePropertyClick = (property: Property) => {
    const themeSkipsLogin = doesThemeSkipLogin(getThemes);
    if (themeSkipsLogin || getToken) {
      navigate(`/listing/${property._id}`);
    } else {
      // Store the current filter state
      const filterState = {
        region: property.region,
        strategy: property.strategy,
        minValue: property.price,
        maxValue: property.price,
        isReserved: property.reserved,
        sortDate: "Latest",
        sortPrice: "By Price",
        sortYield: "By Yield",
        sortDiscount: "By Discount",
        searchKeyword: property.propertyOf,
        activeSort: "date"
      };
      sessionStorage.setItem('filterState', JSON.stringify(filterState));
      
      // Store the intended property ID
      sessionStorage.setItem('intendedPropertyId', property._id);
      navigate('/login');
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent, propertyId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const themeSkipsLogin = doesThemeSkipLogin(getThemes);
    if (themeSkipsLogin) return;

    if (!getToken) {
      sessionStorage.setItem('intendedPropertyId', propertyId);
      navigate("/login");
      return;
    }
    
    try {
      const response = await toggleFavoriteProperty(propertyId, getToken);
      if (response.status === 200) {
        fetchFavorites(); // Refresh favorites list
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  // Determine marker color based on property status
  const getMarkerColor = (property: Property): string => {
    if (property.reserved) return 'gray';
    if (property.discountToMarket > 0) return 'red';
    if (property.occupancy?.toLowerCase() === 'tenanted') return 'blue';
    return 'green';
  };

  return (
    <div className="border border-gray-300 rounded-lg shadow-md overflow-hidden relative">
      {/* Map container is always rendered */}
      <MapContainer
        ref={mapRef}
        center={mapCenter}
        zoom={mapZoom}
        scrollWheelZoom={true}
        maxBounds={UK_BOUNDS}
        maxBoundsViscosity={1.0}
        minZoom={5}
        maxZoom={18}
        style={{ height: 'calc(100vh - 200px)', minHeight: '500px', width: "100%" }}
        whenReady={() => {
          if (mapRef.current) {
            // Force the map to stay within bounds
            mapRef.current.setMaxBounds(UK_BOUNDS);
            // Disable world copy jump
            mapRef.current.options.worldCopyJump = false;
            setMapReady(true);
          }
        }}
      >
        <TileLayer
          url={`https://api.mapbox.com/styles/v1/off2on/cl0hxi4ql000115o9535ccwbr/tiles/{z}/{x}/{y}?access_token=pk.eyJ1Ijoib2ZmMm9uIiwiYSI6ImNrcHM5dTdweTA4YncycG8xbzBxMHlqMWYifQ.QoQslFiXHbvPYZpXavjjew`}
          attribution='Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery &copy; <a href="https://www.mapbox.com/">Mapbox</a>'
        />
        
        {/* Only render markers when all properties are loaded and map is ready */}
        {allPropertiesLoaded && mapReady && propertiesWithCoords.length > 0 && (
          <MarkerClusterGroup chunkedLoading>
            {propertiesWithCoords.map((property) => {
              const isFavorite = favoriteIds.includes(property._id);
              // Per marker: neighbouring pins can carry different sources.
              const valuation = resolveValuationDisplay(getThemes, property);
              return (
                <CircleMarker 
                  key={`property-${property._id}`}
                  center={[Number(property.latitude), Number(property.longitude)]}
                  radius={8}
                  pathOptions={{ 
                    color: getMarkerColor(property),
                    fillColor: getMarkerColor(property),
                    fillOpacity: 0.8
                  }}
                  eventHandlers={{
                    click: () => setSelectedProperty(property)
                  }}
                >
                  <Popup minWidth={300} maxWidth={350}>
                    <div style={{ width: '300px', maxWidth: '300px' }}>
                      {/* Top Bar with Strategy, Yield, and Heart */}
                      <div 
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px',
                          background: getThemes?.color1 || '#4a90e2',
                          color: getThemes?.txtColor || 'white',
                          borderTopLeftRadius: '8px',
                          borderTopRightRadius: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {property.strategy === "Housing Association" ? "H.A" :
                             property.strategy === "Serviced Accommodation" ? "Serviced" :
                             property.strategy}
                          </span>
                          <span style={{ fontWeight: 'bold' }}>
                            {Math.ceil(Number(property.rentGrossYield))}% {property.strategy === "Housing Association" ? "Net" : "Gross"} Yield
                          </span>
                        </div>
                        <button
                          onClick={(e) => handleToggleFavorite(e, property._id)}
                          style={{
                            background: isFavorite 
                              ? `${isWhiteOrShade(getThemes?.color1) ? '#000000' : getThemes?.color1 || '#ffffff'}`
                              : 'rgba(255, 255, 255, 0.9)',
                            padding: '6px',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isFavorite 
                              ? '0 4px 10px rgba(0, 0, 0, 0.25), 0 0 15px rgba(0, 0, 0, 0.2)' 
                              : '0 4px 8px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <FontAwesomeIcon 
                            icon={faHeart} 
                            style={{
                              color: isFavorite 
                                ? '#ffffff'
                                : '#000000',
                              filter: isFavorite ? 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))' : 'none',
                              fontSize: '14px'
                            }}
                          />
                        </button>
                      </div>

                      {/* Property Image - FIXED HEIGHT AND WIDTH */}
                      <div style={{ 
                        width: '100%', 
                        height: '160px', 
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundColor: '#f0f0f0' // Placeholder background for missing images
                      }}>
                        {property.images && property.images.length > 0 ? (
                          <img 
                            src={property.images[0].url} 
                            alt={property.propertyOf} 
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover', // This ensures the image covers the area without distortion
                              objectPosition: 'center' // Centers the image
                            }}
                            onError={(e) => {
                              // Fallback if image fails to load
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: '#999'
                          }}>
                            No Image Available
                          </div>
                        )}
                      </div>

                      {/* Property Details - with fixed heights to maintain consistency */}
                      <div style={{ padding: '12px' }}>
                        <h3 style={{ 
                          fontFamily: 'Roboto, sans-serif', 
                          fontWeight: 500, 
                          fontSize: '16px', 
                          marginBottom: '6px',
                          height: '20px', // Fixed height
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {property.propertyOf}
                        </h3>
                        
                        <p style={{ 
                          color: '#666', 
                          marginBottom: '6px',
                          height: '18px', // Fixed height
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {property.region && property.region !== "false" ? property.region : '\u00A0'}
                        </p>
                        
                        {/* Tags Container - Fixed height */}
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '4px', 
                          marginBottom: '8px',
                          minHeight: '24px' // Reduced minimum height
                        }}>
                          {property.reserved && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              padding: '2px 5px',
                              borderRadius: '3px',
                              background: isWhiteOrShade(getThemes?.color1) ? 'black' : getThemes?.color2 || '#333',
                              color: isWhiteOrShade(getThemes?.color1) ? "white" : getThemes?.txtColor || 'white',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: '1',
                              height: '18px'
                            }}>
                              Reserved
                            </span>
                          )}
                          
                          {property.discountToMarket > 0 && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              padding: '2px 5px',
                              borderRadius: '3px',
                              background: getBackgroundAndTextColor(
                                getThemes?.color1 || '#4a90e2', 
                                getThemes?.color2 || '#333',
                                getThemes?.txtColor || 'white'
                              ).backgroundColor,
                              color: getBackgroundAndTextColor(
                                getThemes?.color1 || '#4a90e2', 
                                getThemes?.color2 || '#333',
                                getThemes?.txtColor || 'white'
                              ).textColor,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: '1',
                              height: '18px'
                            }}>
                              {property.discountToMarket}% Discount
                            </span>
                          )}
                          
                          {property.occupancy?.toLowerCase() === 'tenanted' && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              padding: '2px 5px',
                              borderRadius: '3px',
                              background: getBackgroundAndTextColor(
                                getThemes?.color1 || '#4a90e2', 
                                getThemes?.color2 || '#333',
                                getThemes?.txtColor || 'white'
                              ).backgroundColor,
                              color: getBackgroundAndTextColor(
                                getThemes?.color1 || '#4a90e2', 
                                getThemes?.color2 || '#333',
                                getThemes?.txtColor || 'white'
                              ).textColor,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: '1',
                              height: '18px'
                            }}>
                              Tenanted
                            </span>
                          )}
                          
                          {property.tenureType && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 'bold',
                              padding: '2px 5px',
                              borderRadius: '3px',
                              background: getBackgroundAndTextColor(
                                getThemes?.color1 || '#4a90e2', 
                                getThemes?.color2 || '#333',
                                getThemes?.txtColor || 'white'
                              ).backgroundColor,
                              color: getBackgroundAndTextColor(
                                getThemes?.color1 || '#4a90e2', 
                                getThemes?.color2 || '#333',
                                getThemes?.txtColor || 'white'
                              ).textColor,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: '1',
                              height: '18px'
                            }}>
                              {property.tenureType}
                            </span>
                          )}
                        </div>

                        {/* Info List - Using table layout with fixed heights */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                          <tbody>
                            <tr style={{ height: '26px' }}>
                              <td style={{ padding: '4px 0', fontWeight: 500 }}>Asking Price</td>
                              <td style={{ padding: '4px 0', fontWeight: 'bold', color: '#2e7d32', textAlign: 'right' }}>
                                £{numberFormat(property.price as unknown as number)}
                              </td>
                            </tr>
                            <tr style={{ height: '26px' }}>
                              <td style={{ padding: '4px 0' }}>{valuation.label}</td>
                              <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'right' }}>
                                £{numberFormat(
                                  pickValuationAmount(valuation, {
                                    estimatedValue: property.estimatedValue,
                                    netCashflow: property.netCashflow,
                                  })
                                )}
                              </td>
                            </tr>
                            <tr style={{ height: '26px' }}>
                              <td style={{ padding: '4px 0' }}>Total Investment</td>
                              <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'right' }}>
                                £{numberFormat(Math.round(Number(property.investment)))}
                              </td>
                            </tr>
                            <tr style={{ height: '26px' }}>
                              <td style={{ padding: '4px 0' }}>Rental Income (pcm)</td>
                              <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'right' }}>
                                £{numberFormat(property.averageRent as unknown as number)}
                              </td>
                            </tr>
                          </tbody>
                        </table>

                        {/* Icons and Details */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: '10px',
                          height: '22px' // Fixed height
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {property.bedrooms === 0 && (property.buildingUnits ?? 0) > 0 ? (
                              <>
                                <FontAwesomeIcon icon={faBuilding} />
                                <span>{property.buildingUnits} Units</span>
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faBed} />
                                <span>{property.bedrooms}</span>
                              </>
                            )}
                          </div>
                          {property.floorsqft && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <FontAwesomeIcon icon={faRulerCombined} />
                              <span>{property.floorsqft}</span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => handlePropertyClick(property)}
                          style={{
                            width: '100%',
                            padding: '8px 0',
                            backgroundColor: getThemes?.color2 || '#4a90e2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            height: '36px' // Fixed height
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MarkerClusterGroup>
        )}
      </MapContainer>
      
      {/* Full-page loading overlay that stays until all properties are loaded */}
      {(isLoading || !allPropertiesLoaded || !mapReady) && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <h3 className="text-lg font-semibold mb-2">Loading Properties...</h3>
          {totalCount > 0 && (
            <div className="text-center">
              <p className="mb-2">Processed {processedCount} of {totalCount} properties ({Math.round((processedCount / totalCount) * 100)}%)</p>
              <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500" 
                  style={{ width: `${Math.round((processedCount / totalCount) * 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Only show property count when everything is loaded */}
      {allPropertiesLoaded && (
        <div 
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 500,
            fontSize: '14px'
          }}
        >
          Showing {propertiesWithCoords.length} properties
        </div>
      )}
    </div>
  );
};

export default PropertyMap;